import React, { useState, useEffect, useMemo } from 'react';
import AppIcon from './AppIcon';
import NoteToggle from './NoteToggle';
import DegreesToggle from './DegreesToggle';
import LyricsToggle from './LyricsToggle';
import BeatsToggle from './BeatsToggle';
import UploadIcon from './icons/UploadIcon';
import DownloadIcon from './icons/DownloadIcon';
import ClipboardIcon from './icons/ClipboardIcon';
import ReorderIcon from './icons/ReorderIcon';
import MenuIcon from './icons/MenuIcon';
import ThemeToggleIcon from './icons/ThemeToggleIcon';
import InfoIcon from './icons/InfoIcon';
import ThemeToggleModal from './ThemeToggleModal';
import InfoModal from './InfoModal';
import { useTheme, Theme } from '../contexts/ThemeContext';
import type { KeySignature } from '@chordbaker/music-theory';

// RN-ALIGNED: Clean createStyles function matching React Native pattern
const createStyles = (theme: Theme) => ({
  header: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    zIndex: 50,
    width: '100%',
    paddingTop: theme.spacing.xl,
    paddingBottom: theme.spacing.xl,
    paddingLeft: theme.spacing.xl,
    paddingRight: theme.spacing.xl,
    backgroundColor: theme.colors.headerBackground,
    color: theme.colors.headerText,
    transition: `all ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
  },
  container: {
    maxWidth: '56rem', // 4xl equivalent
    marginLeft: 'auto',
    marginRight: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  leftSection: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  titleSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  appTitle: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: theme.typography.weights.semiBold,
    lineHeight: theme.typography.lineHeights.tight,
    color: theme.colors.headerText,
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
  iconButton: {
    padding: theme.spacing.sm,
    borderRadius: theme.radii.md,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
    transition: `all ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
  },
  mobileMenuContainer: {
    position: 'relative' as const,
  },
  mobileDropdown: {
    position: 'absolute' as const,
    right: 0,
    top: '100%',
    marginTop: theme.spacing.sm,
    width: 224, // 56 * 4 = 224px
    borderRadius: theme.radii.lg,
    boxShadow: theme.shadows.popover,
    paddingVertical: theme.spacing.sm,
    zIndex: 50,
    backgroundColor: theme.colors.modalBackground,
    borderWidth: 1,
    borderStyle: 'solid' as const,
    borderColor: theme.colors.border,
  },
  menuItem: {
    width: '100%',
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.md,
    textAlign: 'left' as const,
    display: 'flex',
    alignItems: 'center',
    transition: `all ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
    color: theme.colors.text,
  },
  menuItemDisabled: {
    opacity: theme.opacity.disabled,
    cursor: 'not-allowed',
  },
  menuIcon: {
    marginRight: theme.spacing.md,
    color: theme.colors.textSecondary,
  },
  divider: {
    marginVertical: theme.spacing.sm,
    borderTopWidth: 1,
    borderTopStyle: 'solid' as const,
    borderTopColor: theme.colors.border,
  },
  loadingSpinner: {
    height: 20,
    width: 20,
    borderWidth: 2,
    borderStyle: 'solid' as const,
    borderRadius: theme.radii.full,
    borderColor: theme.colors.surfaceSecondary,
    borderTopColor: theme.colors.textSecondary,
    animation: 'spin 1s linear infinite',
  },
  hiddenInput: {
    display: 'none',
  },
  desktopOnly: {
    display: 'none',
  },
  mobileOnly: {
    display: 'block',
  },
  // WEB-SPECIFIC: Media query equivalents
  '@media (min-width: 640px)': {
    desktopOnly: {
      display: 'block',
    },
    mobileOnly: {
      display: 'none',
    },
  },
});

interface HeaderProps {
  onSave: () => void;
  onLoad: (file: File) => void;
  onOpenPasteChart: () => void;
  /** Opens the reorder sheet */
  onOpenReorderChart: () => void;
  showNotes: boolean;
  onToggleNotes: () => void;
  showLyrics: boolean;
  onToggleLyrics: () => void;
  showDegrees: boolean;
  onToggleDegrees: () => void;
  showBeats: boolean;
  /** No time signature anywhere: the Beats switch is dimmed and opens the song's time-signature editor. */
  beatsNeedTimeSignature: boolean;
  onToggleBeats: () => void;
  selectedKey: KeySignature | null;
  onSetSelectedKey: (key: KeySignature | null) => void;
  onOpenKeySelector: () => void;
  isLoading?: boolean; // Add loading state prop
}

const Header: React.FC<HeaderProps> = ({
  onSave,
  onLoad,
  onOpenPasteChart,
  onOpenReorderChart,
  showNotes,
  onToggleNotes,
  showLyrics,
  onToggleLyrics,
  showDegrees,
  onToggleDegrees,
  showBeats,
  beatsNeedTimeSignature,
  onToggleBeats,
  selectedKey,
  onSetSelectedKey,
  onOpenKeySelector,
  isLoading = false, // Default to false
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const [showShadow, setShowShadow] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setShowShadow(true);
      } else {
        setShowShadow(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    // Call handler once on mount to set initial state
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onLoad(file);
      event.target.value = ''; // Reset input to allow importing the same file again
    }
  };

  const handleToggleDegreesClick = () => {
    // If no key is selected and degrees are off, open key selector instead of blocking
    if (!selectedKey && !showDegrees) {
      onOpenKeySelector();
    } else {
      // Normal toggle behavior
      onToggleDegrees();
    }
  };


  return (
    <>
      <style>
        {`
          .header-gradient::after {
            background: linear-gradient(to bottom, ${theme.colors.headerBackground}, transparent);
          }
        `}
      </style>
      <header
        className={`header-gradient ${showShadow ? 'after:opacity-100' : 'after:opacity-0'} after:content-[''] after:absolute after:top-full after:left-0 after:right-0 after:h-12 after:pointer-events-none after:transition-opacity after:duration-200`}
        style={styles.header}
      >
        <div style={styles.container}>
          {/* Left Side - RN-aligned structure */}
          <div style={styles.leftSection}>
            <div style={{ marginTop: '-6px' }}>
              <AppIcon size={26} className="rounded-sm" />
            </div>
            <div style={styles.titleSection}>
              <span style={styles.appTitle}>
                ChordBaker
              </span>
            </div>
          </div>

          {/* Right Side - Always visible like React Native 
            Degrees, Tones, Lyrics and Beats switches always visible; the song key is a chip under the title.
            Menu only on mobile for the remaining controls */}
          <div
            style={styles.rightSection}
            className="sm:gap-3" // Apply larger gap on sm+ screens
          >

            {/* Degrees switch — with no key set it opens the key selector instead */}
            <DegreesToggle showDegrees={showDegrees} onToggleDegrees={handleToggleDegreesClick} />

            {/* Notes Toggle Switch - now using shared NoteToggle component like React Native */}
            <div style={{ marginLeft: theme.spacing.sm, display: 'flex', alignItems: 'center' }}>
              <NoteToggle showNotes={showNotes} onToggleNotes={onToggleNotes} />
            </div>

            {/* Lyrics Toggle Switch - matches React Native placement */}
            <div style={{ marginLeft: theme.spacing.sm, display: 'flex', alignItems: 'center' }}>
              <LyricsToggle showLyrics={showLyrics} onToggleLyrics={onToggleLyrics} />
            </div>

            {/* Beats switch (yellow) — with no time signature it opens the song's time-signature editor */}
            <div style={{ marginLeft: theme.spacing.sm, display: 'flex', alignItems: 'center' }}>
              <BeatsToggle
                showBeats={showBeats}
                needsTimeSignature={beatsNeedTimeSignature}
                onToggleBeats={onToggleBeats}
              />
            </div>

            {/* Mobile Menu Button - 3 dots for theme/upload/download - now inside the same container */}
            <div className="sm:hidden relative">
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="p-1.5 rounded-md hover:bg-opacity-50 transition-colors"
                aria-label="Open menu"
              >
                <div style={{ color: theme.colors.textSecondary }}>
                  <MenuIcon className="h-5 w-5" />
                </div>
              </button>

              {/* Mobile Dropdown Menu - only theme and upload/download since key+notes are always visible */}
              {showMobileMenu && (
                <div
                  className="absolute right-0 top-full mt-2 w-56 rounded-lg shadow-lg border py-2 z-50" // Smaller width
                  style={{
                    backgroundColor: theme.colors.modalBackground,
                    borderColor: theme.colors.border,
                  }}
                >
                  {/* Theme Toggle */}
                  <button
                    onClick={() => {
                      setIsThemeModalOpen(true);
                      setShowMobileMenu(false);
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-opacity-50 flex items-center transition-colors text-sm"
                    style={{
                      color: theme.colors.text,
                    }}
                  >
                    <ThemeToggleIcon
                      size={20}
                      isDark={theme.isDark}
                      color={theme.colors.textSecondary}
                      className="mr-3"
                    />
                    <span>Theme</span>
                  </button>

                  {/* Info */}
                  <button
                    onClick={() => {
                      setIsInfoModalOpen(true);
                      setShowMobileMenu(false);
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-opacity-50 flex items-center transition-colors text-sm"
                    style={{
                      color: theme.colors.text,
                    }}
                  >
                    <InfoIcon
                      size={20}
                      color={theme.colors.textSecondary}
                      className="mr-3"
                    />
                    <span>Info</span>
                  </button>

                  <div className="my-2" style={{ borderTopColor: theme.colors.border, borderTopWidth: '1px' }}></div>

                  {/* Upload */}
                  <label
                    htmlFor="chord-sheet-upload-mobile"
                    className={`w-full px-4 py-3 text-left flex items-center transition-colors text-sm ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-opacity-50 cursor-pointer'}`}
                    style={{ color: theme.colors.text }}
                  >
                    {isLoading ? (
                      <div
                        className="h-5 w-5 animate-spin rounded-full border-2 mr-3"
                        style={{
                          borderColor: theme.colors.surfaceSecondary,
                          borderTopColor: theme.colors.textSecondary,
                        }}
                      ></div>
                    ) : (
                      <div className="mr-3" style={{ color: theme.colors.textSecondary }}>
                        <UploadIcon className="h-5 w-5" />
                      </div>
                    )}
                    <span>Import Song</span>
                  </label>
                  <input
                    type="file"
                    id="chord-sheet-upload-mobile"
                    accept=".chordbaker,.json"
                    onChange={(e) => {
                      handleFileChange(e);
                      setShowMobileMenu(false);
                    }}
                    className="hidden"
                    disabled={isLoading}
                  />

                  {/* Paste chart (plain text → chart) */}
                  <button
                    onClick={() => {
                      onOpenPasteChart();
                      setShowMobileMenu(false);
                    }}
                    disabled={isLoading}
                    className={`w-full px-4 py-3 text-left flex items-center transition-colors text-sm ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-opacity-50'}`}
                    style={{ color: theme.colors.text }}
                  >
                    <div className="mr-3" style={{ color: theme.colors.textSecondary }}>
                      <ClipboardIcon className="h-5 w-5" />
                    </div>
                    <span>Paste Chart</span>
                  </button>

                  {/* Reorder chart (the reorder sheet) */}
                  <button
                    onClick={() => {
                      onOpenReorderChart();
                      setShowMobileMenu(false);
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-opacity-50 flex items-center transition-colors text-sm"
                    style={{ color: theme.colors.text }}
                  >
                    <div className="mr-3" style={{ color: theme.colors.textSecondary }}>
                      <ReorderIcon size={20} />
                    </div>
                    <span>Reorder Chart</span>
                  </button>

                  {/* Download */}
                  <button
                    onClick={() => {
                      onSave();
                      setShowMobileMenu(false);
                    }}
                    disabled={isLoading}
                    className={`w-full px-4 py-3 text-left flex items-center transition-colors text-sm ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-opacity-50'}`}
                    style={{ color: theme.colors.text }}
                  >
                    {isLoading ? (
                      <div
                        className="h-5 w-5 animate-spin rounded-full border-2 mr-3"
                        style={{
                          borderColor: theme.colors.surfaceSecondary,
                          borderTopColor: theme.colors.textSecondary,
                        }}
                      ></div>
                    ) : (
                      <div className="mr-3" style={{ color: theme.colors.textSecondary }}>
                        <DownloadIcon className="h-5 w-5" />
                      </div>
                    )}
                    <span>Export Song</span>
                  </button>
                </div>
              )}
            </div>

            {/* Desktop-only buttons - Theme, Info, Upload, Download */}
            <button
              onClick={() => setIsThemeModalOpen(true)}
              className="hidden sm:block p-1.5 rounded-md hover:bg-opacity-50 transition-colors"
              title="Change theme"
              aria-label="Change Theme"
            >
              <ThemeToggleIcon
                size={20}
                isDark={theme.isDark}
                color={theme.colors.textSecondary}
              />
            </button>

            <button
              onClick={() => setIsInfoModalOpen(true)}
              className="hidden sm:block p-1.5 rounded-md hover:bg-opacity-50 transition-colors"
              title="About ChordBaker"
              aria-label="About ChordBaker"
            >
              <InfoIcon
                size={20}
                color={theme.colors.textSecondary}
              />
            </button>

            <label
              htmlFor="chord-sheet-upload"
              title={isLoading ? "Processing..." : "Import song"}
              className={`hidden sm:block p-1.5 rounded-md transition-colors ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-opacity-50 cursor-pointer'}`}
              aria-label="Import Song"
            >
              {isLoading ? (
                <div
                  className="h-5 w-5 animate-spin rounded-full border-2"
                  style={{
                    borderColor: theme.colors.surfaceSecondary,
                    borderTopColor: theme.colors.textSecondary,
                  }}
                ></div>
              ) : (
                <div style={{ color: theme.colors.textSecondary }}>
                  <UploadIcon className="h-5 w-5" />
                </div>
              )}
            </label>
            <input
              type="file"
              id="chord-sheet-upload"
              accept=".chordbaker,.json"
              onChange={handleFileChange}
              className="hidden"
              disabled={isLoading}
            />
            <button
              onClick={onOpenPasteChart}
              disabled={isLoading}
              title="Paste chart"
              className={`hidden sm:block p-1.5 rounded-md transition-colors ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-opacity-50'}`}
              aria-label="Paste Chart"
            >
              <div style={{ color: theme.colors.textSecondary }}>
                <ClipboardIcon className="h-5 w-5" />
              </div>
            </button>
            <button
              onClick={onOpenReorderChart}
              title="Reorder chart"
              className="hidden sm:block p-1.5 rounded-md hover:bg-opacity-50 transition-colors"
              aria-label="Reorder chart"
            >
              <div style={{ color: theme.colors.textSecondary }}>
                <ReorderIcon size={20} />
              </div>
            </button>
            <button
              onClick={onSave}
              disabled={isLoading}
              title={isLoading ? "Processing..." : "Export song"}
              className={`hidden sm:block p-1.5 rounded-md transition-colors ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-opacity-50'}`}
              aria-label="Export Song"
            >
              {isLoading ? (
                <div
                  className="h-5 w-5 animate-spin rounded-full border-2"
                  style={{
                    borderColor: theme.colors.surfaceSecondary,
                    borderTopColor: theme.colors.textSecondary,
                  }}
                ></div>
              ) : (
                <div style={{ color: theme.colors.textSecondary }}>
                  <DownloadIcon className="h-5 w-5" />
                </div>
              )}
            </button>
          </div>
        </div>

        {/* Theme Toggle Modal */}
        <ThemeToggleModal
          visible={isThemeModalOpen}
          onClose={() => setIsThemeModalOpen(false)}
        />

        {/* Info Modal */}
        <InfoModal
          visible={isInfoModalOpen}
          onClose={() => setIsInfoModalOpen(false)}
        />
      </header>
    </>
  );
};

export default Header;
